import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, combineLatest } from "rxjs";
import { filter, map } from "rxjs/operators";
import { RouteConfigInstance, wsRoutes } from "../routes.config";
import { OnlyProGuard } from "./guards/only-pro.guard";
import { AccountsService } from "@services/accounts.service";
import { TranslateService } from "@ngx-translate/core";
import { feature } from "@web/app/modules/feature-toggle/feature-toggle.util";

export interface NavigationState {
  title: string;
  subtitle?: string;
  tooltip?: string;
  icon?: string;
  showTitle: boolean;
  showBreadcrumb: boolean;
  breadcrumb: Array<{ label: string; link: string }>;
  backUrl: string | null;
  showBack: boolean;
  menuItems: MenuItem[];
  menuItemKey: string;
  menuSubItemKey: string;
  showSubtitle: boolean;
  subTitleIcon: string;
  showBackButton: boolean;
  /** From route config: force stacked subheader even without shell subtitle. */
  stackedSubheader: boolean;
}

export interface MenuItem {
  key: string;
  label: string;
  icon: string;
  route?: string;
  permission?: string;
  permissionSectionBase?: string;
  disabledIfNotPro?: boolean;
  disabled?: boolean;
  subItems?: {
    label: string;
    route: string;
    disabledIfNotPro?: boolean;
    disabled?: boolean;
    permission?: string;
    permissionSectionBase?: string;
    action?: () => void;
  }[];
}

@Injectable({
  providedIn: "root",
})
export class WsNavigatorService {

  private _menuItems: MenuItem[] = [];
  private _adminAsWinery: boolean = false;

  get menuItems(): MenuItem[] {
    return this._menuItems;
  }

  private updateMenuItems() {
    let items = wsRoutes.filter(x => (x.showInMenu ?? false) && (!x.feature || feature.on(x.feature))).map((route) => ({
      key: route.path == "" && route.children ? route.children.map((child) => child.path).join("|") : route.path,
      label: route.label || route.subtitle || "",
      icon: route.icon || "",
      route: route.path || "",
      permission: route.permission,
      permissionSectionBase: route.permissionSectionBase,
      disabledIfNotPro: this.disabledIfNotPro(route),
      subItems: route.children?.filter( (y) => y.showInMenu).map((child) => ({
        label: this.translateService.instant(child.label || child.subtitle || ""),
        route: child.path
          ? route.path + (route.path ? "/" : "") + child.path
          : (route.path || ""),
        permission: child.permission ?? route.permission,
        permissionSectionBase: child.permissionSectionBase ?? route.permissionSectionBase,
        disabledIfNotPro: this.disabledIfNotPro(route) || this.disabledIfNotPro(child),
      })),
    }));

    // Menu Admin ora gestito direttamente nel componente ws-menu

    this._menuItems = items;
    // Update navigation state with new menu items
    const currentState = this.navigationState.value;
    this.navigationState.next({
      ...currentState,
      menuItems: this._menuItems
    });
  }

  disabledIfNotPro(item: RouteConfigInstance): boolean {
    return !!(item.proOnly || (item.canLoad && item.canLoad.some((x) => x === OnlyProGuard)));
  }

  private navigationState = new BehaviorSubject<NavigationState>({
    title: "",
    subtitle: "",
    tooltip: "",
    icon: "",
    showTitle: true,
    showBreadcrumb: true,
    breadcrumb: [],
    backUrl: null,
    showBack: false,
    menuItems: this.menuItems,
    menuItemKey: "",
    menuSubItemKey: "",
    showSubtitle: false,
    subTitleIcon: "",
    showBackButton: false,
    stackedSubheader: false,
  });

  navigationState$ = this.navigationState.asObservable();

  /** Current shell navigation snapshot (title, breadcrumb, …). */
  get currentState(): NavigationState {
    return this.navigationState.value;
  }

  constructor(private router: Router, private accountsService: AccountsService, private translateService: TranslateService) {
    // Initialize menu items
    this.updateMenuItems();

    // Subscribe to authorization changes
    this.accountsService.authorization$.subscribe(auth => {
      const newAdminAsWinery = !!(auth?.is_admin && auth?.is_winery);
      if (this._adminAsWinery !== newAdminAsWinery) {
        this._adminAsWinery = newAdminAsWinery;
        this.updateMenuItems();
      }
    });

    // Subscribe to language changes
    this.translateService.onLangChange.subscribe(() => {
      this.updateMenuItems();
      // Update current navigation state with new translations
      const currentUrl = this.router.url;
      this.updateNavigationState(currentUrl);
    });

    // Aggiorna lo state del navigation ad ogni NavigationEnd
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if(event instanceof NavigationEnd){
          this.updateNavigationState(event.urlAfterRedirects);
        }
      });
  }

  private buildRoutePath(parentPath: string | undefined, routePath: string | undefined): string {
    if (parentPath === undefined || parentPath === '') {
      return routePath || '/';
    }
    if (!routePath) {
      return parentPath;
    }
    return `${parentPath}/${routePath}`;
  }

  private childSearchPath(parentPath: string | undefined, routePath: string | undefined): string {
    if (!parentPath) {
      return routePath ?? '';
    }
    if (!routePath) {
      return parentPath;
    }
    return `${parentPath}/${routePath}`;
  }

  private elementMatchingPath(path: string, routes: RouteConfigInstance[], fatherPath?: string): RouteConfigInstance | null {
    // Rimuovi i query params dall'URL per il matching
    const cleanPath = path.split('?')[0].split('#')[0];
    
    const currentRoutes = routes.filter((route) => {
      const fullPath = this.buildRoutePath(fatherPath, route.path);
      const urlTree = this.router.parseUrl(fullPath || '/');
      const isActive = this.router.isActive(urlTree, false);
      return isActive;
    });
    
    if (!currentRoutes?.length) {
      return null;
    }
    
    if (currentRoutes.length === 1 && !currentRoutes[0].children?.length) {
      return currentRoutes[0];
    }
    
    // Se abbiamo multiple routes (es. parent con path vuoto e altri), cerca nei children
    let childAndFather = currentRoutes
      .filter(currentRoute => !!currentRoute.children?.length)
      .map((currentRoute) => {
        const childPath = this.childSearchPath(fatherPath, currentRoute.path);
        return {
          father: currentRoute,
          child: this.elementMatchingPath(cleanPath, currentRoute.children!, childPath),
        };
      })
      .find((x) => !!x.child) ?? {
        father:
          currentRoutes.find(x => !x.children?.length) ??
          currentRoutes.find(x => !!x.children?.length) ??
          currentRoutes[0],
        child: null,
      };
    
    return {
      ... childAndFather.father!,
      ... childAndFather.child,
      path: childAndFather.child
        ? this.buildRoutePath(childAndFather.father?.path, childAndFather.child.path)
        : (childAndFather.father?.path ?? ''),
    };
  }

  isLastSection(item: {route?: string, subItems?: any[]}): boolean {
    if (!item.subItems || item.subItems.length === 0) {
      const route = item.route ?? "";
      return this.isMenuRouteActive(route, this.getSiblingRoutes(route));
    }

    return item.subItems.some(child => this.isLastSection(child));
  }

  private getSiblingRoutes(route: string): string[] {
    for (const menuItem of this._menuItems) {
      if (menuItem.subItems?.some((sub) => sub.route === route)) {
        return menuItem.subItems.map((sub) => sub.route);
      }
    }
    return [];
  }

  private isMenuRouteActive(route: string, siblingRoutes: string[]): boolean {
    const urlTree = this.router.parseUrl(route);
    if (!this.router.isActive(urlTree, false)) {
      return false;
    }

    const moreSpecificSiblingActive = siblingRoutes.some(
      (siblingRoute) =>
        siblingRoute !== route &&
        siblingRoute.startsWith(route + "/") &&
        this.router.isActive(this.router.parseUrl(siblingRoute), false)
    );

    return !moreSpecificSiblingActive;
  }

  private updateNavigationState(url: string) {
    const pathOnly = (url || "").split("?")[0].split("#")[0];
    const segments = pathOnly.split("/").filter(Boolean); // Remove empty segments
    
    let matchedConfig = this.elementMatchingPath(url, wsRoutes);
    if (!matchedConfig) {
      matchedConfig = {
        label: "Unknown Page",
        subtitle: "",
        icon: "help",
        showInMenu: false,
        path: url,
      };
    }

    let breadcrumb = segments.map((segment, index) => {
      const path = segments.slice(0, index + 1).join("/");
      const config = this.elementMatchingPath(path, wsRoutes);
      return {
        label: config?.subtitle || config?.label || segment,
        link: "/" + path,
      };
    });

    const deepestSnapshot = this.getDeepestActivatedRouteSnapshot(this.router.routerState.snapshot.root);
    if (deepestSnapshot && deepestSnapshot.data && deepestSnapshot.data.pageName) {
      breadcrumb[breadcrumb.length - 1].label = deepestSnapshot.data.pageName;
    }
      
    const newState: NavigationState = {
      title: matchedConfig.label ? this.translateService.instant(matchedConfig.label) : "",
      subtitle: matchedConfig.subtitle ? this.translateService.instant(matchedConfig.subtitle) : "",
      tooltip: matchedConfig.tooltip ? this.translateService.instant(matchedConfig.tooltip) : "",
      icon:  "",
      subTitleIcon: "",
      showTitle: !!matchedConfig.label,
      showBreadcrumb: !!matchedConfig.showBreadcrumb,
      breadcrumb: breadcrumb.map(item => ({
        ...item,
        label: this.translateService.instant(item.label)
      })),
      backUrl:
        segments.length > 1 ? "/" + segments.slice(0, -1).join("/") : null,
      showBack: segments.length > 1,
      menuItems: this.menuItems,
      menuItemKey: "",
      menuSubItemKey: "",
      showSubtitle: matchedConfig.showSubtitle !== false && !!matchedConfig.subtitle,
      showBackButton:
        matchedConfig.showBackButton === false
          ? false
          : !!matchedConfig.showBreadcrumb && segments.length > 1,
      stackedSubheader: !!matchedConfig.stackedSubheader,
    };

    this.navigationState.next(newState);
  }

  private getDeepestActivatedRouteSnapshot(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    return snapshot;
  }

  haveToShowTitle(): boolean {
    return this.navigationState.value.showTitle;
  }

  haveToShowBreadcrumb(): boolean {
    return this.navigationState.value.showBreadcrumb;
  }

  haveToShowBack(): boolean {
    return this.navigationState.value.showBack;
  }

  haveToShowSubtitle(): boolean {
    return this.navigationState.value.showSubtitle;
  }

  updateSubtitle(subtitle: string): void {
    const currentState = this.navigationState.value;
    this.navigationState.next({
      ...currentState,
      subtitle: subtitle,
      showSubtitle: !!subtitle
    });
  }

  setBreadcrumb(breadcrumb: Array<{ label: string; link: string }>): void {
    const currentState = this.navigationState.value;
    this.navigationState.next({
      ...currentState,
      showBreadcrumb: true,
      breadcrumb: (breadcrumb || []).map((item) => ({
        label: item.label,
        link: item.link,
      })),
    });
  }

  updateLastBreadcrumb(label: string): void {
    const currentState = this.navigationState.value;
    const breadcrumb = (currentState.breadcrumb || []).slice();
    if (!label) {
      return;
    }
    if (!breadcrumb.length) {
      breadcrumb.push({ label, link: this.router.url });
    } else {
      breadcrumb[breadcrumb.length - 1] = { ...breadcrumb[breadcrumb.length - 1], label };
    }
    this.navigationState.next({
      ...currentState,
      showBreadcrumb: true,
      breadcrumb,
    });
  }
  
}